import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Scale, ArrowLeft, Plus, Sparkles, Loader2, ChevronDown, Wand2, Check, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { auth } from '../utils/auth';
import { casesApi, clientsApi, aiApi } from '../services';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'arbeidsrett', label: 'Arbeidsrett' },
  { value: 'familie & arv', label: 'Familie & arv' },
  { value: 'kontraktsrett', label: 'Kontraktsrett' },
  { value: 'eiendom', label: 'Eiendom' },
  { value: 'erstatning', label: 'Erstatning' },
  { value: 'annet', label: 'Annet' }
];

const CreateCase = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    topic: '',
    description: '',
    category: '',
    case_type: 'FULL'
  });
  
  // AI assistance states
  const [aiMode, setAiMode] = useState(false);
  const [keywords, setKeywords] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [showAiPreview, setShowAiPreview] = useState(false);

  useEffect(() => {
    fetchClients();
    
    // Check if client is pre-selected from URL
    const params = new URLSearchParams(window.location.search);
    const preSelectedClient = params.get('client');
    if (preSelectedClient) {
      setFormData({ ...formData, client_id: preSelectedClient });
    }
  }, []);

  const fetchClients = async () => {
    try {
      const currentUser = auth.getUser();
      
      if (!currentUser) {
        console.error('User not authenticated');
        navigate('/lexy/login');
        return;
      }
      
      // Use axios with withCredentials for cookie auth
      const response = await axios.get(`${API}/crm/clients/${currentUser.id}`);
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Could not fetch clients:', error);
      if (error.response?.status === 401) {
        toast.error('Du må logge inn først');
        navigate('/lexy/login');
      }
    }
  };

  const handleGenerateWithAI = async () => {
    if (!keywords.trim()) {
      toast.error('Skriv inn noen stikkord først');
      return;
    }
    
    setAiGenerating(true);
    
    try {
      const selectedClient = clients.find(c => c.id === formData.client_id);
      const clientName = selectedClient 
        ? (selectedClient.type === 'PERSON' ? selectedClient.person_name : selectedClient.company_name)
        : null;
      
      // Use axios with withCredentials for cookie auth
      const response = await axios.post(`${API}/ai/cases/expand-description`, {
        keywords: keywords,
        client_name: clientName,
        case_type: formData.category
      });
      
      setAiSuggestion(response.data);
      setShowAiPreview(true);
      toast.success('✨ AI-forslag generert!');
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Kunne ikke generere beskrivelse');
    } finally {
      setAiGenerating(false);
    }
  };
  
  const handleAcceptAI = () => {
    if (aiSuggestion) {
      setFormData({
        ...formData,
        description: aiSuggestion.expanded_description,
        topic: aiSuggestion.suggested_title || formData.topic,
        category: aiSuggestion.suggested_category || formData.category
      });
      setShowAiPreview(false);
      setAiMode(false);
      toast.success('✅ AI-forslag godkjent');
    }
  };
  
  const handleRejectAI = () => {
    setShowAiPreview(false);
    setAiSuggestion(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const currentUser = auth.getUser();
    if (!currentUser) {
      toast.error('Du må logge inn først');
      navigate('/lexy/login');
      return;
    }
    
    try {
      // Show loading
      toast.loading('Oppretter sak...');
      
      // Use axios with withCredentials for cookie auth
      const response = await axios.post(`${API}/cases`, {
        topic: formData.topic,
        intake_summary: formData.description,
        client_id: formData.client_id,
        category: formData.category
      });
      
      toast.dismiss();
      toast.success('✅ Sak opprettet!');
      
      console.log('Case created:', response.data);
      
      // Redirect to case workspace if we have the case ID
      if (response.data && response.data.id) {
        navigate(`/lexy/case/${response.data.id}/workspace`);
      } else {
        // Fallback to dashboard
        navigate('/lexy/dashboard?tab=cases');
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error creating case:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette sak');
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header - Linear style */}
      <header className="bg-white border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/lexy/dashboard')} 
              className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              <span className="font-semibold text-slate-900">Ny sak</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          
          {/* Client Selection */}
          <div className="bg-white border border-slate-200/60 rounded-lg p-4 sm:p-6 shadow-sm">
            <label className="block text-sm font-semibold text-slate-900 mb-3">Klient</label>
            <div className="flex gap-2">
              <select
                value={formData.client_id}
                onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                className="flex-1 h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-600 focus:border-transparent"
                required
              >
                <option value="">Velg klient...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.type === 'PERSON' ? client.person_name : client.company_name}
                    {client.type === 'COMPANY' ? ' (Bedrift)' : ''}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                onClick={() => navigate('/lexy/clients/new')}
                variant="outline"
                className="h-10"
              >
                <Plus className="w-4 h-4 mr-1" />
                Ny
              </Button>
            </div>
          </div>

          {/* Category Selection */}
          <div className="bg-white border border-slate-200/60 rounded-lg p-4 sm:p-6 shadow-sm">
            <label className="block text-sm font-semibold text-slate-900 mb-3">Kategori</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full h-10 px-3 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-600 focus:border-transparent"
            >
              <option value="">Velg kategori...</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div className="bg-white border border-slate-200/60 rounded-lg p-4 sm:p-6 shadow-sm">
            <label className="block text-sm font-semibold text-slate-900 mb-3">Sakstittel</label>
            <Input
              value={formData.topic}
              onChange={(e) => setFormData({...formData, topic: e.target.value})}
              placeholder="F.eks: Arbeidskonflikt - oppsigelse"
              className="h-10 text-sm"
              required
            />
          </div>

          {/* Description with AI toggle */}
          <div className="bg-white border border-slate-200/60 rounded-lg shadow-sm">
            <div className="p-4 sm:p-6 pb-4">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-semibold text-slate-900">Saksbeskrivelse</label>
                <button
                  type="button"
                  onClick={() => setAiMode(!aiMode)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    aiMode 
                      ? 'bg-slate-100 text-slate-800' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {aiMode ? 'AI-modus aktiv' : 'Bruk AI-assistent'}
                </button>
              </div>

              {!aiMode ? (
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full h-40 p-3 text-sm border border-slate-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Beskriv saken i detalj..."
                  required
                />
              ) : (
                <div className="space-y-3">
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-slate-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                      <Wand2 className="w-5 h-5 text-slate-900 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-indigo-900 mb-2">
                          AI-assistert saksbeskrivelse
                        </p>
                        <p className="text-xs text-slate-800 mb-3">
                          Skriv korte stikkord, så utvider AI dem til en full beskrivelse
                        </p>
                        <textarea
                          value={keywords}
                          onChange={(e) => setKeywords(e.target.value)}
                          className="w-full h-24 p-3 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-slate-600 bg-white"
                          placeholder="F.eks: Klient sagt opp uten varsel. Jobbet 3 år. Mener oppsigelse ugyldig. Arbeidsgiver sier brudd på lojalitet."
                        />
                        <Button
                          type="button"
                          onClick={handleGenerateWithAI}
                          disabled={aiGenerating || !keywords.trim()}
                          className="mt-3 bg-slate-900 hover:bg-slate-800 text-white h-9 text-sm"
                        >
                          {aiGenerating ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Genererer...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Generer med AI
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* AI Preview */}
                  {showAiPreview && aiSuggestion && (
                    <div className="border-2 border-slate-300 rounded-lg p-4 bg-white shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-slate-900" />
                          <span className="text-sm font-semibold text-slate-900">AI-forslag</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            aiSuggestion.confidence === 'HIGH' 
                              ? 'bg-green-100 text-green-700'
                              : aiSuggestion.confidence === 'MEDIUM'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {aiSuggestion.confidence === 'HIGH' ? 'Høy tillit' : 
                             aiSuggestion.confidence === 'MEDIUM' ? 'Middels tillit' : 'Lav tillit'}
                          </span>
                        </div>
                      </div>
                      
                      {aiSuggestion.suggested_title && (
                        <div className="mb-2">
                          <p className="text-xs text-slate-500 mb-1">Foreslått tittel:</p>
                          <p className="text-sm font-medium text-slate-900">{aiSuggestion.suggested_title}</p>
                        </div>
                      )}
                      
                      <div className="mb-3">
                        <p className="text-xs text-slate-500 mb-1">Beskrivelse:</p>
                        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 p-3 rounded border border-slate-200">
                          {aiSuggestion.expanded_description}
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Button
                          type="button"
                          onClick={handleAcceptAI}
                          className="flex-1 bg-slate-900 hover:bg-slate-800 h-10 text-sm font-medium"
                        >
                          <Check className="w-4 h-4 mr-2" />
                          Bruk dette
                        </Button>
                        <Button
                          type="button"
                          onClick={handleRejectAI}
                          variant="outline"
                          className="flex-1 sm:flex-initial h-10 text-sm font-medium"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Avvis
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              onClick={() => navigate('/lexy/dashboard')}
              variant="outline"
              className="w-full sm:flex-1 h-11 text-base font-medium"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              disabled={!formData.description}
              className="w-full sm:flex-1 bg-slate-900 hover:bg-slate-800 h-11 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Opprett sak
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateCase;
